import React, { useState, useMemo, useCallback } from "react";
import { useUnprocessed } from "../hooks/useUnprocessed";
import { QueryParams } from "../types/api";
import { rasService } from "../services/rasService";
import StableSelect from "./StableSelect";
import UncontrolledInput from "./UncontrolledInput";
import { debugLog, logActiveElement } from "../utils/debug";

// Available columns for filtering unprocessed races (matching actual API response)
const AVAILABLE_COLUMNS = {
    // Numeric fields
    id: { label: "ID", type: "numeric" },
    totalhorses: { label: "Total Horses", type: "numeric" },
    totalhorsesprocessed: { label: "Horses Processed", type: "numeric" },

    // Date fields
    date: { label: "Race Date", type: "date" },

    // Text fields - using database column names for filtering
    track_country: { label: "Track Country", type: "text" },
    track_name: { label: "Track Name", type: "text" },
    event_name: { label: "Event Name", type: "text" },
    event_group: { label: "Event Group", type: "text" },
    meeting_id: { label: "Meeting ID", type: "text" },
    event_id: { label: "Event ID", type: "text" },
} as const;

// Columns that are actually returned by the API for display
const DISPLAY_COLUMNS = {
    id: { label: "ID", type: "numeric" },
    date: { label: "Date", type: "date" },
    track_country: { label: "Country", type: "text" },
    trackname: { label: "Track Name", type: "text" },
    eventname: { label: "Event Name", type: "text" },
    eventgroup: { label: "Event Group", type: "text" },
    totalhorses: { label: "Total Horses", type: "numeric" },
    totalhorsesprocessed: { label: "Horses Processed", type: "numeric" },
    meetingid: { label: "Meeting ID", type: "text" },
    eventid: { label: "Event ID", type: "text" },
} as const;

type ColumnKey = keyof typeof AVAILABLE_COLUMNS;
type ColumnType = (typeof AVAILABLE_COLUMNS)[ColumnKey]["type"];
type DisplayColumnKey = keyof typeof DISPLAY_COLUMNS;

const OPERATORS_BY_TYPE: Record<ColumnType, Array<{ value: string; label: string }>> = {
    text: [
        { value: "=", label: "Equals" },
        { value: "!=", label: "Not Equals" },
        { value: "LIKE", label: "Contains" },
        // { value: "ILIKE", label: "Contains (case-insensitive)" },
    ],
    numeric: [
        { value: "=", label: "Equals" },
        { value: "!=", label: "Not Equals" },
        { value: ">", label: "Greater Than" },
        { value: ">=", label: "Greater Than or Equal" },
        { value: "<", label: "Less Than" },
        { value: "<=", label: "Less Than or Equal" },
    ],
    date: [
        { value: "=", label: "On Date" },
        { value: "!=", label: "Not On Date" },
        { value: ">", label: "After" },
        { value: ">=", label: "On or After" },
        { value: "<", label: "Before" },
        { value: "<=", label: "On or Before" },
    ],
};

interface FilterRow {
    id: string;
    column: ColumnKey;
    operator: string;
    value: string;
}

interface SortConfig {
    field: DisplayColumnKey;
    direction: "asc" | "desc" | null;
}

const UnprocessedPanel: React.FC = React.memo(() => {
    debugLog("UnprocessedPanel", "Rendering component");

    const { data, loading, error, count, queryUnprocessed, clearResults } = useUnprocessed();
    const [filters, setFilters] = useState<FilterRow[]>([{ id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }]);
    const [limit, setLimit] = useState<number>(66);
    const [sortConfig, setSortConfig] = useState<SortConfig>({ field: "id", direction: null });

    // Selection state
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [batchProcessing, setBatchProcessing] = useState(false);

    // Quick filter presets
    const quickFilters = [
        {
            label: "Recent (Last 7 Days)",
            filters: [{ column: "date" as ColumnKey, operator: ">=", value: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0] }],
        },
        {
            label: "Today",
            filters: [{ column: "date" as ColumnKey, operator: "=", value: new Date().toISOString().split("T")[0] }],
        },
        {
            label: "US",
            filters: [{ column: "track_country" as ColumnKey, operator: "=", value: "US" }],
        },
        {
            label: "AU",
            filters: [{ column: "track_country" as ColumnKey, operator: "=", value: "AU" }],
        },
        {
            label: "UK",
            filters: [{ column: "track_country" as ColumnKey, operator: "=", value: "UK" }],
        },
        {
            label: "Stakes",
            filters: [{ column: "event_group" as ColumnKey, operator: "!=", value: "Non-Stakes" }],
        },
        {
            label: "Non-Stakes",
            filters: [{ column: "event_group" as ColumnKey, operator: "=", value: "Non-Stakes" }],
        },
    ];

    const columnOptions = useMemo(
        () =>
            Object.entries(AVAILABLE_COLUMNS).map(([key, config]) => ({
                value: key,
                label: config.label,
            })),
        []
    );

    const getOperatorOptions = useCallback((columnKey: ColumnKey) => {
        const columnType = AVAILABLE_COLUMNS[columnKey]?.type || "text";
        return OPERATORS_BY_TYPE[columnType] || OPERATORS_BY_TYPE.text;
    }, []);

    const addFilter = useCallback(() => {
        debugLog("UnprocessedPanel", "Adding new filter");
        setFilters((prev) => [
            ...prev,
            {
                id: crypto.randomUUID(),
                column: "date",
                operator: ">=",
                value: "",
            },
        ]);
    }, []);

    const removeFilter = useCallback((filterId: string) => {
        debugLog("UnprocessedPanel", "Removing filter", filterId);
        setFilters((prev) => {
            const remaining = prev.filter((f) => f.id !== filterId);
            // If this would remove the last filter, replace it with a default empty filter
            if (remaining.length === 0) {
                return [{ id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }];
            }
            return remaining;
        });
    }, []);

    const updateFilter = useCallback(
        (filterId: string, field: keyof FilterRow, value: string) => {
            debugLog("UnprocessedPanel", "Updating filter", { filterId, field, value });
            logActiveElement("UnprocessedPanel", "Before filter update");

            setFilters((prev) =>
                prev.map((filter) => {
                    if (filter.id === filterId) {
                        const updated = { ...filter, [field]: value };
                        // Reset operator when column changes
                        if (field === "column") {
                            const newColumnKey = value as ColumnKey;
                            const availableOperators = getOperatorOptions(newColumnKey);
                            updated.operator = availableOperators[0]?.value || "=";
                        }
                        return updated;
                    }
                    return filter;
                })
            );
        },
        [getOperatorOptions]
    );

    const applyQuickFilter = useCallback((preset: (typeof quickFilters)[0]) => {
        debugLog("UnprocessedPanel", "Applying quick filter", preset.label);
        const newFilters = preset.filters.map((f) => ({
            id: crypto.randomUUID(),
            column: f.column,
            operator: f.operator,
            value: f.value,
        }));
        setFilters(newFilters);
    }, []);

    const clearFilters = useCallback(() => {
        debugLog("UnprocessedPanel", "Clearing all filters");
        setFilters([{ id: crypto.randomUUID(), column: "date", operator: ">=", value: "" }]);
        setSelectedRows(new Set());
        clearResults();
    }, [clearResults]);

    // Selection helper functions
    const handleSelectRow = useCallback((id: number, checked: boolean) => {
        setSelectedRows((prev) => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(id);
            } else {
                newSet.delete(id);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(
        (checked: boolean) => {
            if (checked) {
                setSelectedRows(new Set(data.map((record) => record.id)));
            } else {
                setSelectedRows(new Set());
            }
        },
        [data]
    );

    const allSelected = data.length > 0 && selectedRows.size === data.length;
    const someSelected = selectedRows.size > 0 && selectedRows.size < data.length;

    const handleSearch = useCallback(() => {
        debugLog("UnprocessedPanel", "Handling search", { filters, limit });

        const queryParams: QueryParams = { limit };

        filters.forEach((filter) => {
            if (filter.value.trim()) {
                // For '=' operator, use just the column name as key
                // For other operators, append the operator to the column name
                const paramKey = filter.operator === "=" ? filter.column : `${filter.column}${filter.operator}`;
                (queryParams as any)[paramKey] = filter.value.trim();
            }
        });

        debugLog("UnprocessedPanel", "Query params built", queryParams);
        setSelectedRows(new Set());
        queryUnprocessed(queryParams);
    }, [filters, limit, queryUnprocessed]);

    // Batch processing function
    const handleBatchProcess = useCallback(async () => {
        if (selectedRows.size === 0) return;

        setBatchProcessing(true);

        try {
            const selectedRecords = data.filter((record) => selectedRows.has(record.id));
            const processPromises = selectedRecords.map((record) =>
                rasService.processRaceData({
                    meeting_id: (record as any).meetingid,
                    event_id: (record as any).eventid,
                })
            );

            // Fire all requests simultaneously
            Promise.all(processPromises);

            // Show simple confirmation and clear selection
            setSelectedRows(new Set());
        } catch (error) {
            console.error("Batch processing error:", error);
        } finally {
            setBatchProcessing(false);
        }
    }, [selectedRows, data]);

    const handleSort = (field: DisplayColumnKey) => {
        setSortConfig((prev) => {
            if (prev.field !== field) {
                return { field, direction: "asc" };
            }

            switch (prev.direction) {
                case null:
                    return { field, direction: "asc" };
                case "asc":
                    return { field, direction: "desc" };
                case "desc":
                    return { field, direction: null };
                default:
                    return { field, direction: "asc" };
            }
        });
    };

    const getSortIcon = (field: DisplayColumnKey) => {
        if (sortConfig.field !== field) return "↕";
        switch (sortConfig.direction) {
            case "asc":
                return "↑";
            case "desc":
                return "↓";
            default:
                return "↕";
        }
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleString();
    };

    const formatCellValue = useCallback((value: any): string => {
        if (value === null || value === undefined) return "";
        if (typeof value === "boolean") return value ? "Yes" : "No";
        if (typeof value === "object") return JSON.stringify(value);
        return String(value);
    }, []);

    // Sort the data
    const sortedData = useMemo(() => {
        if (!data.length || !sortConfig.direction) return data;

        const sorted = [...data].sort((a, b) => {
            const aValue = (a as any)[sortConfig.field];
            const bValue = (b as any)[sortConfig.field];

            // Handle null/undefined values
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortConfig.direction === "asc" ? -1 : 1;
            if (bValue == null) return sortConfig.direction === "asc" ? 1 : -1;

            // Get column type for proper sorting
            const columnType = DISPLAY_COLUMNS[sortConfig.field].type;

            let comparison = 0;
            if (columnType === "numeric") {
                comparison = Number(aValue) - Number(bValue);
            } else if (columnType === "date") {
                comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
            } else {
                comparison = String(aValue).localeCompare(String(bValue));
            }

            return sortConfig.direction === "asc" ? comparison : -comparison;
        });

        return sorted;
    }, [data, sortConfig]);

    // Export to CSV functionality
    const exportToCSV = useCallback(() => {
        if (!data.length) return;

        const headers = Object.keys(data[0]).join(",");
        const csvContent = data
            .map((record) =>
                Object.values(record)
                    .map((value) => {
                        const stringValue = formatCellValue(value);
                        return stringValue.includes(",") ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
                    })
                    .join(",")
            )
            .join("\n");

        const blob = new Blob([`${headers}\n${csvContent}`], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `unprocessed-races-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }, [data, formatCellValue]);

    if (error) {
        return (
            <div className="panel-content">
                <h2 className="panel-title">Unprocessed Query</h2>
                <div className="error-message">Error: {error}</div>
            </div>
        );
    }

    return (
        <div className="panel-content">
            {/* <h2 className="panel-title">Unprocessed Query</h2> */}

            {/* Quick Filters */}
            <div style={{ marginBottom: "20px" }}>
                <h3>Quick Filters</h3>
                <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "15px" }}>
                    {quickFilters.map((preset, index) => (
                        <button
                            key={index}
                            onClick={() => applyQuickFilter(preset)}
                            style={{
                                padding: "8px 12px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#f8f9fa",
                                cursor: "pointer",
                            }}
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Advanced Filters */}
            <div style={{ marginBottom: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
                    <h3>Advanced Filters</h3>
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            onClick={addFilter}
                            style={{
                                padding: "6px 12px",
                                border: "1px solid #007bff",
                                borderRadius: "4px",
                                backgroundColor: "#007bff",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            + Add Filter
                        </button>
                        <button
                            onClick={clearFilters}
                            style={{
                                padding: "6px 12px",
                                border: "1px solid #6c757d",
                                borderRadius: "4px",
                                backgroundColor: "#6c757d",
                                color: "white",
                                cursor: "pointer",
                            }}
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Filter Rows */}
                {filters.map((filter, index) => (
                    <div
                        key={filter.id}
                        style={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                            marginBottom: "10px",
                            padding: "10px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            backgroundColor: "#f9f9f9",
                        }}
                    >
                        <span style={{ minWidth: "30px", color: "#666" }}>#{index + 1}</span>

                        <div style={{ minWidth: "200px" }}>
                            <StableSelect
                                id={`column-${filter.id}`}
                                value={filter.column}
                                onChange={(value) => updateFilter(filter.id, "column", value)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                <option value="">Select column...</option>
                                {columnOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </StableSelect>
                        </div>

                        <div style={{ minWidth: "150px" }}>
                            <StableSelect
                                id={`operator-${filter.id}`}
                                value={filter.operator}
                                onChange={(value) => updateFilter(filter.id, "operator", value)}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            >
                                <option value="">Select operator...</option>
                                {getOperatorOptions(filter.column).map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </StableSelect>
                        </div>

                        <div style={{ flex: 1, minWidth: "200px", maxWidth: "calc(100% - 250px)" }}>
                            <UncontrolledInput
                                id={`value-${filter.id}`}
                                type={AVAILABLE_COLUMNS[filter.column]?.type === "date" ? "date" : "text"}
                                value={filter.value}
                                onChange={(value) => updateFilter(filter.id, "value", value)}
                                placeholder={`Enter ${AVAILABLE_COLUMNS[filter.column]?.label.toLowerCase()}...`}
                                style={{ width: "100%", padding: "8px", border: "1px solid #ddd", borderRadius: "4px" }}
                            />
                        </div>

                        <div style={{ flexShrink: 0, marginLeft: "16px" }}>
                            <button
                                onClick={() => removeFilter(filter.id)}
                                style={{
                                    padding: "6px 10px",
                                    border: "1px solid #dc3545",
                                    borderRadius: "4px",
                                    backgroundColor: "#dc3545",
                                    color: "white",
                                    cursor: "pointer",
                                }}
                            >
                                ×
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Search Controls */}
            <div
                style={{
                    display: "flex",
                    gap: "15px",
                    alignItems: "center",
                    marginBottom: "20px",
                    padding: "15px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#f8f9fa",
                }}
            >
                <div>
                    <label htmlFor="limit" style={{ marginRight: "8px", fontWeight: "bold" }}>
                        Limit:
                    </label>
                    <UncontrolledInput
                        id="limit"
                        type="number"
                        value={limit.toString()}
                        onChange={(value) => setLimit(parseInt(value) || 66)}
                        style={{ width: "80px", padding: "6px", border: "1px solid #ddd", borderRadius: "4px" }}
                    />
                </div>

                <button
                    onClick={handleSearch}
                    disabled={loading}
                    style={{
                        padding: "10px 20px",
                        border: "none",
                        borderRadius: "4px",
                        backgroundColor: loading ? "#ccc" : "#28a745",
                        color: "white",
                        cursor: loading ? "not-allowed" : "pointer",
                        fontWeight: "bold",
                    }}
                >
                    {loading ? (
                        <>
                            <span className="loading-spinner" style={{ marginRight: "8px" }}></span>
                            Searching...
                        </>
                    ) : (
                        "Search Unprocessed"
                    )}
                </button>

                {data.length > 0 && (
                    <button
                        onClick={exportToCSV}
                        style={{
                            padding: "10px 16px",
                            border: "1px solid #17a2b8",
                            borderRadius: "4px",
                            backgroundColor: "#17a2b8",
                            color: "white",
                            cursor: "pointer",
                        }}
                    >
                        Export CSV
                    </button>
                )}

                {selectedRows.size > 0 && (
                    <button
                        onClick={handleBatchProcess}
                        disabled={batchProcessing}
                        style={{
                            padding: "10px 16px",
                            border: "1px solid #ffc107",
                            borderRadius: "4px",
                            backgroundColor: batchProcessing ? "#ccc" : "#ffc107",
                            color: batchProcessing ? "#666" : "#212529",
                            cursor: batchProcessing ? "not-allowed" : "pointer",
                            fontWeight: "bold",
                        }}
                    >
                        {batchProcessing ? (
                            <>
                                <span className="loading-spinner" style={{ marginRight: "8px" }}></span>
                                Sending Requests...
                            </>
                        ) : (
                            `Process Selected (${selectedRows.size})`
                        )}
                    </button>
                )}
            </div>

            {/* Results */}
            {data.length > 0 && (
                <div style={{ border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#ffffff", overflow: "hidden" }}>
                    <div style={{ padding: "15px", backgroundColor: "#f8f9fa", borderBottom: "1px solid #e9ecef" }}>
                        <h3 style={{ margin: 0 }}>Search Results</h3>
                        <p style={{ margin: "5px 0 0 0", color: "#6c757d" }}>
                            Found {count || 0} records {data && `(showing ${data.length})`}
                            {sortConfig.direction && (
                                <span style={{ marginLeft: "10px" }}>
                                    | Sorted by {DISPLAY_COLUMNS[sortConfig.field].label} ({sortConfig.direction})
                                </span>
                            )}
                        </p>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1000px" }}>
                            <thead style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}>
                                <tr>
                                    <th
                                        style={{
                                            padding: "12px 8px",
                                            textAlign: "center",
                                            borderBottom: "1px solid #e9ecef",
                                            backgroundColor: "#f8f9fa",
                                            width: "50px",
                                            fontSize: "12px",
                                            fontWeight: "600",
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={(checkbox) => {
                                                if (checkbox) checkbox.indeterminate = someSelected;
                                            }}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                            style={{ cursor: "pointer" }}
                                        />
                                        <div style={{ fontSize: "10px", marginTop: "2px", color: "#666" }}>
                                            {selectedRows.size > 0 ? `(${selectedRows.size})` : ""}
                                        </div>
                                    </th>
                                    {Object.entries(DISPLAY_COLUMNS).map(([key, config]) => (
                                        <th
                                            key={key}
                                            onClick={() => handleSort(key as DisplayColumnKey)}
                                            style={{
                                                padding: "12px 8px",
                                                textAlign: "left",
                                                borderBottom: "1px solid #e9ecef",
                                                cursor: "pointer",
                                                userSelect: "none",
                                                backgroundColor: sortConfig.field === key ? "#e3f2fd" : "#f8f9fa",
                                                minWidth: "100px",
                                                fontSize: "12px",
                                                fontWeight: "600",
                                            }}
                                        >
                                            {config.label} {getSortIcon(key as DisplayColumnKey)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((record, index) => (
                                    <tr key={record.id} style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8f9fa" }}>
                                        <td
                                            style={{
                                                padding: "8px",
                                                borderBottom: "1px solid #e9ecef",
                                                textAlign: "center",
                                                width: "50px",
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedRows.has(record.id)}
                                                onChange={(e) => handleSelectRow(record.id, e.target.checked)}
                                                style={{ cursor: "pointer" }}
                                            />
                                        </td>
                                        {Object.entries(DISPLAY_COLUMNS).map(([key]) => {
                                            const value = (record as any)[key];
                                            const formattedValue = key === "date" && typeof value === "string" ? formatDate(value) : formatCellValue(value);

                                            return (
                                                <td
                                                    key={key}
                                                    style={{
                                                        padding: "8px",
                                                        borderBottom: "1px solid #e9ecef",
                                                        fontSize: "12px",
                                                        maxWidth: key === "trackname" || key === "eventname" ? "200px" : "120px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                    title={formattedValue}
                                                >
                                                    {key === "totalhorses" || key === "totalhorsesprocessed" ? (
                                                        <span
                                                            style={{
                                                                color: "#28a745",
                                                                fontWeight: "500",
                                                                fontSize: "11px",
                                                                textAlign: "center",
                                                                display: "block",
                                                            }}
                                                        >
                                                            {formattedValue}
                                                        </span>
                                                    ) : (
                                                        formattedValue
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && data.length === 0 && count === 0 && (
                <div style={{ border: "1px solid #e9ecef", borderRadius: "8px", backgroundColor: "#ffffff", overflow: "hidden" }}>
                    <div style={{ padding: "40px", textAlign: "center", color: "#6c757d" }}>
                        <p>No unprocessed races found matching your search criteria</p>
                        <p>
                            <em>Use the quick filters above or add custom filters to search for unprocessed races.</em>
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
});

UnprocessedPanel.displayName = "UnprocessedPanel";

export default UnprocessedPanel;
